/**
 * Diagnostics: what the backend says about itself, who is signed in, and what
 * the API answers with and without that person's token.
 *
 * This is the screen the first real Entra sign-in in the fleet is proven on
 * (phase 4 doc, section 6). It shows three things side by side so a failure
 * is recognisable: the backend's mode and registration, the session state
 * with the signed-in UPN, and `GET /cash-drop/submissions` observed twice,
 * once with no credentials and once with the acquired bearer token. Under
 * `entra` the first should be a 401 and the second a 200.
 */
import { useEffect, useState } from "react";

import { fetchHealth, observeSubmissions, type HealthOut, type Observed, type SubmissionsOut } from "../api/public";
import { useSession } from "../auth/session";

type HealthState = { kind: "loading" } | { kind: "ready"; health: HealthOut } | { kind: "error"; message: string };

export function Diagnostics() {
  const session = useSession();
  const [healthState, setHealthState] = useState<HealthState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    fetchHealth()
      .then((health) => {
        if (!cancelled) setHealthState({ kind: "ready", health });
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setHealthState({ kind: "error", message: error instanceof Error ? error.message : String(error) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (session.kind === "loading" || healthState.kind === "loading") {
    return <p className="muted">Reading backend configuration…</p>;
  }

  if (session.kind === "error" || healthState.kind === "error") {
    const message = session.kind === "error" ? session.message : healthState.kind === "error" ? healthState.message : "";
    return (
      <section className="card error">
        <h2>Cannot reach the backend</h2>
        <p>{message}</p>
        <p className="muted">
          The API is served from this same origin, so this means the backend process is
          down or the dev proxy is not pointed at it. It is not a network permission problem.
        </p>
      </section>
    );
  }

  const { health } = healthState;
  const { config } = session;

  return (
    <>
      <section className="card">
        <h2>Backend</h2>
        <dl className="facts">
          <dt>Status</dt>
          <dd data-testid="status">{health.status}</dd>
          <dt>Version</dt>
          <dd>{health.version}</dd>
          <dt>Auth mode</dt>
          <dd data-testid="auth-mode">{health.auth_mode}</dd>
          <dt>Locations</dt>
          <dd>{health.locations}</dd>
          <dt>Cash drop submissions</dt>
          <dd>{health.cash_drop_submissions}</dd>
          <dt>Safe count submissions</dt>
          <dd data-testid="safe-count-submissions">{health.safe_count_submissions}</dd>
          <dt>Petty cash entries</dt>
          <dd data-testid="petty-cash-entries">{health.petty_cash_entries}</dd>
        </dl>

        <h2>Sign-in configuration</h2>
        <dl className="facts">
          <dt>Authority</dt>
          <dd>{config.authority ?? "—"}</dd>
          <dt>Client id</dt>
          <dd data-testid="client-id">{config.client_id ?? "not configured"}</dd>
          <dt>Scopes</dt>
          <dd>{config.scopes.length ? config.scopes.join(", ") : "—"}</dd>
          <dt>Staff domain</dt>
          <dd>{config.allowed_upn_domain}</dd>
          <dt>Redirect URI</dt>
          <dd>{window.location.origin}</dd>
        </dl>
      </section>

      <SignInCard />
      <SubmissionsCard />
    </>
  );
}

function SignInCard() {
  const session = useSession();

  switch (session.kind) {
    case "mock":
      return (
        <section className="card">
          <h2>Sign-in</h2>
          <p data-testid="session-state">
            <strong>Mock mode.</strong> The backend is trusting a header, so there is nothing to
            sign in to. Every request is the mock principal.
          </p>
        </section>
      );
    case "unconfigured":
      return (
        <section className="card error">
          <h2>Sign-in</h2>
          <p data-testid="session-state">
            <strong>Sign-in is not available.</strong> The backend is in <code>entra</code> mode
            but <code>/auth/config</code> returned no client id, so there is nothing to
            initialise MSAL with. Set <code>AUTH_API_CLIENT_ID</code> on the backend.
          </p>
        </section>
      );
    case "signing-in":
      return (
        <section className="card">
          <h2>Sign-in</h2>
          <p data-testid="session-state" className="muted">
            Completing sign-in…
          </p>
        </section>
      );
    case "signed-out":
      return (
        <section className="card">
          <h2>Sign-in</h2>
          <p data-testid="session-state">Not signed in.</p>
          <button type="button" onClick={() => void session.signIn()}>
            Sign in
          </button>
          <p className="muted">
            Redirects to Microsoft, then back to this origin. Staff accounts only.
          </p>
        </section>
      );
    case "signed-in":
      return (
        <section className="card">
          <h2>Sign-in</h2>
          <p data-testid="session-state">
            Signed in as <strong data-testid="upn">{session.upn}</strong>
          </p>
          <button type="button" onClick={() => void session.signOut()}>
            Sign out
          </button>
        </section>
      );
    default:
      return null;
  }
}

type Probe = { kind: "idle" } | { kind: "running" } | { kind: "done"; anonymous: Observed<SubmissionsOut>; bearer: Observed<SubmissionsOut> | null; tokenError: string | null };

function SubmissionsCard() {
  const session = useSession();
  const [probe, setProbe] = useState<Probe>({ kind: "idle" });

  const signedIn = session.kind === "signed-in";
  const getAccessToken = signedIn ? session.getAccessToken : null;

  useEffect(() => {
    if (session.kind === "loading" || session.kind === "error" || session.kind === "signing-in") return;
    let cancelled = false;
    setProbe({ kind: "running" });
    (async () => {
      const anonymous = await observeSubmissions();
      let bearer: Observed<SubmissionsOut> | null = null;
      let tokenError: string | null = null;
      if (getAccessToken) {
        try {
          bearer = await observeSubmissions(await getAccessToken());
        } catch (error) {
          tokenError = error instanceof Error ? error.message : String(error);
        }
      }
      if (!cancelled) setProbe({ kind: "done", anonymous, bearer, tokenError });
    })();
    return () => {
      cancelled = true;
    };
  }, [session.kind, getAccessToken]);

  if (probe.kind !== "done") {
    return probe.kind === "running" ? <p className="muted">Probing the API…</p> : null;
  }

  return (
    <section className="card">
      <h2>
        <code>GET /cash-drop/submissions</code>
      </h2>
      <dl className="facts">
        <dt>No credentials</dt>
        <dd data-testid="anonymous-result">{describe(probe.anonymous)}</dd>
        <dt>With bearer token</dt>
        <dd data-testid="bearer-result">
          {probe.tokenError
            ? `token could not be acquired: ${probe.tokenError}`
            : probe.bearer
              ? describe(probe.bearer)
              : signedIn
                ? "—"
                : "not signed in"}
        </dd>
      </dl>
    </section>
  );
}

function describe(observed: Observed<SubmissionsOut>): string {
  if (observed.ok && observed.body && "count" in observed.body) {
    return `${observed.status} · ${observed.body.count} submission(s)`;
  }
  const detail = observed.body && "detail" in observed.body ? observed.body.detail : undefined;
  return detail ? `${observed.status} · ${detail}` : String(observed.status);
}
